import React, { useRef, useEffect } from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import { makeStyles } from '@material-ui/core/styles';
import { FieldInputProps } from 'react-final-form';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { TextFieldWithMention } from './TextFieldWithMention';
import { MentionItem } from 'react-mentions';

// With an extra control the field gets a bottom toolbar inside its border (extra control next to the mic, both
// right-aligned) so the controls never sit on top of the text.
const useStyles = makeStyles({
  inputWithToolbar: {
    flexWrap: 'wrap',
    // !important: MUI's own multiline/dense padding rule has the same specificity
    paddingBottom: '0 !important',
    // room for a line or two of text above the toolbar even when the field is empty
    '& .mentionsInput__control': {
      minHeight: 40
    }
  },
  toolbar: {
    // runs edge to edge inside the field border (cancels the field's 14px side padding) as a divider line
    width: 'calc(100% + 28px)',
    margin: '8px -14px 0',
    padding: '2px 8px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    // MUI's InputAdornment is height:0.01em - without this the row collapses and the divider cuts through the controls
    height: 'auto',
    maxHeight: 'none',
    justifyContent: 'flex-end',
    gap: 4,
    '& svg': {
      fontSize: 20
    }
  }
});

interface SpeechTextFieldWithMarkupProps {
  input: FieldInputProps<any, HTMLElement>;
   onChange: (
    event: { target: { value: string } },
    newValue: string,
    newPlainTextValue: string,
    mentions: MentionItem[]
  ) => void;
  label?: string;
  className?: string;
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
  markup?: string;
  isLoadData: boolean;
  onlyUsedByEnquiries?: boolean;
  // an extra control shown in a bottom toolbar inside the field, left of the microphone (e.g. the canned notes picker)
  endAdornmentExtra?: React.ReactNode;
}

export const SpeechTextFieldWithMarkup: React.FC<SpeechTextFieldWithMarkupProps> = ({ 
  input, 
  onChange,
  ...props }) => {
  const classes = useStyles();
  const valueRef = useRef(input.value);

  // Update the ref whenever input.value changes
  useEffect(() => {
    valueRef.current = input.value;
  }, [input.value]);

  const { listening, startSpeechRecognition, stopSpeechRecognition } = useSpeechRecognition(
    (transcript) => {
      const currentValue = valueRef.current || '';
      const newValue = currentValue ? `${currentValue} ${transcript}` : transcript;

      // First: Update Final Form state
      input.onChange(newValue);

      // Then: Trigger any external side effects
      onChange(
        { target: { value: newValue } } as any,
        newValue,
        newValue,
        []
      );
    }
  );

  return (
    <TextFieldWithMention
        // {...input}
        // {...props}
        value={input.value} 
        onChange={onChange}
        label={props.label}
        className={props.className}
        variant={props.variant}
        size={props.size}
        markup={props.markup}
        isLoadData={props.isLoadData}
        onlyUsedByEnquiries={props.onlyUsedByEnquiries}
        InputProps={{
          className: props.endAdornmentExtra ? classes.inputWithToolbar : undefined,
          endAdornment: (
            <InputAdornment position="end" className={props.endAdornmentExtra ? classes.toolbar : undefined}>
              {props.endAdornmentExtra}
              {!listening ? (
                <IconButton size={props.endAdornmentExtra ? 'small' : undefined} onClick={startSpeechRecognition}>
                  <MicOffIcon />
                </IconButton>
              ) : (
                <IconButton size={props.endAdornmentExtra ? 'small' : undefined} onClick={stopSpeechRecognition}>
                  <MicIcon />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
    />
  );
};
